import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { MagnifyingGlass, FolderLock, CaretRight, LockKey } from "@phosphor-icons/react";

const ROLE_BADGE = {
  admin: "bg-primary/10 text-primary border-primary/20",
  manager: "bg-warning/10 text-warning border-warning/20",
  employee: "bg-muted text-muted-foreground border-border",
};

export default function File201List() {
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: { q: q || undefined, include_resigned: true } });
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="file201-list-page">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
          <FolderLock weight="duotone" className="h-7 w-7 text-primary-foreground" />
        </div>
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Confidential Records</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">201 File Folder</h1>
          <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-sm">
            <LockKey className="h-4 w-4" /> Restricted access. Sensitive fields are encrypted and visible to administrators only.
          </p>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="file201-search" placeholder="Search employee..." className="pl-9"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Role</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No employees found.</TableCell></TableRow>
              ) : employees.map((emp) => (
                <TableRow key={emp.id} data-testid={`file201-row-${emp.id}`} className="cursor-pointer animate-fade-up"
                  onClick={() => navigate(`/file201/${emp.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-xs shrink-0">
                        {emp.picture ? <img src={emp.picture} alt="" className="h-9 w-9 rounded-full object-cover" /> :
                          emp.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{emp.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{emp.email}</p>
                        {emp.employment_status === "resigned" && (
                          <span className="inline-flex items-center gap-1.5 mt-1">
                            <Badge variant="outline" className="font-medium bg-destructive/10 text-destructive border-destructive/20" data-testid={`file201-resigned-badge-${emp.id}`}>Resigned</Badge>
                            {emp.resign_date && <span className="text-xs text-destructive">{emp.resign_date}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{emp.employee_id || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{emp.department || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{emp.position || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={`capitalize font-medium ${ROLE_BADGE[emp.role]}`}>{emp.role}</Badge></TableCell>
                  <TableCell className="text-right"><CaretRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
